import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { BaseFieldWrapper } from './BaseFieldWrapper';
import { LinkProperties } from 'src/api/process-module';
import { Field } from 'src/API';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import toast from 'react-hot-toast';

interface LinkFieldProps {
  field: Field;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  fieldProps?: LinkProperties;
}

export const LinkField = ({ field, value, onChange, disabled = false, fieldProps }: LinkFieldProps) => {
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value || '');
      toast.success('Link copied to clipboard');
    } catch (err) {
      console.error('Failed to copy link:', err);
      toast.error('Failed to copy link');
    }
  };

  const handleOpen = () => {
    if (!value) {
      toast.error('No URL to open');
      return;
    }

    // Add http:// if no protocol is specified
    const url = value.match(/^https?:\/\//) ? value : `http://${value}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <BaseFieldWrapper label={field.label} required={fieldProps?.required} fieldProps={fieldProps}>
      <TextField
        fullWidth
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder={fieldProps?.override?.placeholder}
        variant="outlined"
        type="url"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title="Copy link">
                <IconButton onClick={handleCopy} disabled={!value} size="small" sx={{ mr: 0.5 }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open link">
                <IconButton onClick={handleOpen} disabled={!value} size="small">
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'background.paper',
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
            '&:hover fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.24)',
            },
            '&.Mui-focused fieldset': {
              borderColor: 'primary.main',
            },
          },
        }}
      />
    </BaseFieldWrapper>
  );
};
